import {
  FacilityInfo,
  FuelType,
  GasStationSupplier,
  OwnershipType,
  SelfServiceType,
} from '../../../models/FuelingService.ts';
import { ExistenceStatus } from '../../../models/PlaceService.ts';
import { useForm } from '@mantine/form';
import { Button, Checkbox, Group, Stack, TextInput } from '@mantine/core';
import React from 'react';

export interface MutateFuelingServiceFuelingInfoFormValues {
  supplier: GasStationSupplier;
  ownershipType: OwnershipType;
  supportedFuelTypes: FuelType[];
  selfServiceTypes: SelfServiceType[];
  hasRestroom: ExistenceStatus;
  hasAccessibleRestroom: ExistenceStatus;
  airPumpInfo: FacilityInfo;
  parkingLotInfo: FacilityInfo;
  carWashInfo: FacilityInfo;
  carMaintenanceInfo: FacilityInfo;
  convenienceStoreInfo: FacilityInfo;
  waterPumpInfo: FacilityInfo;
}

interface MutateFuelingServiceFuelingInfoFormProps {
  initialValues: MutateFuelingServiceFuelingInfoFormValues;
  onSubmit: (formValues: MutateFuelingServiceFuelingInfoFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
}

export default function MutateFuelingServiceFuelingInfoForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitButtonText,
}: MutateFuelingServiceFuelingInfoFormProps) {
  const form = useForm<MutateFuelingServiceFuelingInfoFormValues>({
    mode: 'controlled',
    initialValues: {
      supplier: initialValues.supplier,
      ownershipType: initialValues.ownershipType,
      supportedFuelTypes: initialValues.supportedFuelTypes,
      selfServiceTypes: initialValues.selfServiceTypes,
      hasRestroom: initialValues.hasRestroom,
      hasAccessibleRestroom: initialValues.hasAccessibleRestroom,
      airPumpInfo: initialValues.airPumpInfo,
      parkingLotInfo: initialValues.parkingLotInfo,
      carWashInfo: initialValues.carWashInfo,
      carMaintenanceInfo: initialValues.carMaintenanceInfo,
      convenienceStoreInfo: initialValues.convenienceStoreInfo,
      waterPumpInfo: initialValues.waterPumpInfo,
    },
    validate: {
      supplier: (value) => {
        if (!value) return ' ';
      },
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Stack gap={16}>
          <Group gap={16} style={{ paddingBottom: 8 }}>
            {/* 油品供應商 */}
            <Stack gap={8}>
              <Group gap={0}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  油品供應商
                </div>
                <div style={{ color: 'red' }}>*</div>
              </Group>
              <Group gap={12} w={305.5}>
                {/* 台塑 */}
                <Checkbox
                  radius={'xl'}
                  label={'台塑'}
                  checked={form.values.supplier === 'FPCC'}
                  onChange={() => {
                    form.setFieldValue(
                      'supplier',
                      form.values.supplier === 'FPCC' ? null : 'FPCC',
                    );
                  }}
                  styles={{
                    label: { paddingLeft: 8 },
                    input: {
                      borderColor: form.errors.supplier
                        ? 'var(--mantine-color-red-6)'
                        : undefined,
                    },
                  }}
                />
                {/* 中油 */}
                <Checkbox
                  radius={'xl'}
                  label={'中油'}
                  checked={form.values.supplier === 'CPC'}
                  onChange={() => {
                    form.setFieldValue(
                      'supplier',
                      form.values.supplier === 'CPC' ? null : 'CPC',
                    );
                  }}
                  styles={{
                    label: { paddingLeft: 8 },
                    input: {
                      borderColor: form.errors.supplier
                        ? 'var(--mantine-color-red-6)'
                        : undefined,
                    },
                  }}
                />
              </Group>
            </Stack>
            {/* 供應商類型 */}
            <Stack gap={8}>
              <Group gap={0}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  供應商類型
                </div>
              </Group>
              <Group gap={12} w={305.5}>
                {/* 直營 */}
                <Checkbox
                  radius={'xl'}
                  label={'直營'}
                  checked={form.values.ownershipType === 'CORPORATE'}
                  onChange={() => {
                    form.setFieldValue(
                      'ownershipType',
                      form.values.ownershipType === 'CORPORATE'
                        ? null
                        : 'CORPORATE',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 加盟 */}
                <Checkbox
                  radius={'xl'}
                  label={'加盟'}
                  checked={form.values.ownershipType === 'FRANCHISE'}
                  onChange={() => {
                    form.setFieldValue(
                      'ownershipType',
                      form.values.ownershipType === 'FRANCHISE'
                        ? null
                        : 'FRANCHISE',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Stack>
          </Group>

          <Group gap={16} style={{ paddingBottom: 8 }}>
            {/* 支援油種 */}
            <Checkbox.Group
              label={'支援油種'}
              key={form.key('supportedFuelTypes')}
              {...form.getInputProps('supportedFuelTypes')}
              styles={{
                root: {
                  width: 305.5,
                },
                label: {
                  paddingBottom: 8,
                  lineHeight: '20px',
                },
              }}
            >
              <Group gap={12}>
                <Checkbox
                  value={'RON_92'}
                  label={'92'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                <Checkbox
                  value={'RON_95'}
                  label={'95'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                <Checkbox
                  value={'RON_98'}
                  label={'98'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                <Checkbox
                  value={'DIESEL'}
                  label={'超柴'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                <Checkbox
                  value={'E3_GASOHOL'}
                  label={'酒精'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Checkbox.Group>
            {/* 自助加油 */}
            <Checkbox.Group
              label={'自助加油'}
              key={form.key('selfServiceTypes')}
              {...form.getInputProps('selfServiceTypes')}
              styles={{
                root: {
                  width: 305.5,
                },
                label: {
                  paddingBottom: 8,
                  lineHeight: '20px',
                },
              }}
            >
              <Group gap={12}>
                <Checkbox
                  value={'GAS_SELF_SERVICE'}
                  label={'汽油'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                <Checkbox
                  value={'DIESEL_SELF_SERVICE'}
                  label={'柴油'}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Checkbox.Group>
          </Group>

          <Group gap={16} style={{ paddingBottom: 8 }}>
            {/* 廁所 */}
            <Stack gap={8}>
              <Group gap={0}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  廁所
                </div>
              </Group>
              <Group gap={12} w={305.5}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={form.values.hasRestroom === 'EXISTS'}
                  onChange={() => {
                    form.setFieldValue(
                      'hasRestroom',
                      form.values.hasRestroom === 'EXISTS' ? null : 'EXISTS',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={form.values.hasRestroom === 'NOT_EXIST'}
                  onChange={() => {
                    form.setFieldValue(
                      'hasRestroom',
                      form.values.hasRestroom === 'NOT_EXIST'
                        ? null
                        : 'NOT_EXIST',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Stack>
            {/* 無障礙廁所 */}
            <Stack gap={8}>
              <Group gap={0}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  無障礙廁所
                </div>
              </Group>
              <Group gap={12} w={305.5}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={form.values.hasAccessibleRestroom === 'EXISTS'}
                  onChange={() => {
                    form.setFieldValue(
                      'hasAccessibleRestroom',
                      form.values.hasAccessibleRestroom === 'EXISTS'
                        ? null
                        : 'EXISTS',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={form.values.hasAccessibleRestroom === 'NOT_EXIST'}
                  onChange={() => {
                    form.setFieldValue(
                      'hasAccessibleRestroom',
                      form.values.hasAccessibleRestroom === 'NOT_EXIST'
                        ? null
                        : 'NOT_EXIST',
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
              </Group>
            </Stack>
          </Group>

          <Group gap={16} style={{ paddingBottom: 8 }}>
            {/* 充氣 */}
            <Stack gap={8}>
              <Group gap={0}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  充氣
                </div>
              </Group>
              <Group gap={12} w={305.5} h={32}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={form.values.airPumpInfo.existenceStatus === 'EXISTS'}
                  onChange={() => {
                    form.setFieldValue(
                      'airPumpInfo',
                      form.values.airPumpInfo.existenceStatus === 'EXISTS'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'EXISTS',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={
                    form.values.airPumpInfo.existenceStatus === 'NOT_EXIST'
                  }
                  onChange={() => {
                    form.setFieldValue(
                      'airPumpInfo',
                      form.values.airPumpInfo.existenceStatus === 'NOT_EXIST'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'NOT_EXIST',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 營業時間 */}
                {form.values.airPumpInfo.existenceStatus === 'EXISTS' && (
                  <TextInput
                    placeholder={'請輸入營業時間'}
                    key={form.key('airPumpInfo.operationHoursDescription')}
                    {...form.getInputProps(
                      'airPumpInfo.operationHoursDescription',
                    )}
                    size={'xs'}
                    styles={{
                      input: { width: 190 },
                    }}
                  />
                )}
              </Group>
            </Stack>
            {/* 停車場 */}
            <Stack gap={8}>
              <Group gap={0}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  停車場
                </div>
              </Group>
              <Group gap={12} w={305.5} h={32}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={
                    form.values.parkingLotInfo.existenceStatus === 'EXISTS'
                  }
                  onChange={() => {
                    form.setFieldValue(
                      'parkingLotInfo',
                      form.values.parkingLotInfo.existenceStatus === 'EXISTS'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'EXISTS',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={
                    form.values.parkingLotInfo.existenceStatus === 'NOT_EXIST'
                  }
                  onChange={() => {
                    form.setFieldValue(
                      'parkingLotInfo',
                      form.values.parkingLotInfo.existenceStatus === 'NOT_EXIST'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'NOT_EXIST',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 營業時間 */}
                {form.values.parkingLotInfo.existenceStatus === 'EXISTS' && (
                  <TextInput
                    placeholder={'請輸入營業時間'}
                    key={form.key('parkingLotInfo.operationHoursDescription')}
                    {...form.getInputProps(
                      'parkingLotInfo.operationHoursDescription',
                    )}
                    size={'xs'}
                    styles={{
                      input: { width: 190 },
                    }}
                  />
                )}
              </Group>
            </Stack>
          </Group>

          <Group gap={16} style={{ paddingBottom: 8 }}>
            {/* 洗車 */}
            <Stack gap={8}>
              <Group gap={0}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  洗車
                </div>
              </Group>
              <Group gap={12} w={305.5} h={32}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={form.values.carWashInfo.existenceStatus === 'EXISTS'}
                  onChange={() => {
                    form.setFieldValue(
                      'carWashInfo',
                      form.values.carWashInfo.existenceStatus === 'EXISTS'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'EXISTS',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={
                    form.values.carWashInfo.existenceStatus === 'NOT_EXIST'
                  }
                  onChange={() => {
                    form.setFieldValue(
                      'carWashInfo',
                      form.values.carWashInfo.existenceStatus === 'NOT_EXIST'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'NOT_EXIST',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 營業時間 */}
                {form.values.carWashInfo.existenceStatus === 'EXISTS' && (
                  <TextInput
                    placeholder={'請輸入營業時間'}
                    key={form.key('carWashInfo.operationHoursDescription')}
                    {...form.getInputProps(
                      'carWashInfo.operationHoursDescription',
                    )}
                    size={'xs'}
                    styles={{
                      input: { width: 190 },
                    }}
                  />
                )}
              </Group>
            </Stack>
            {/* 汽車保養 */}
            <Stack gap={8}>
              <Group gap={0}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  汽車保養
                </div>
              </Group>
              <Group gap={12} w={305.5} h={32}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={
                    form.values.carMaintenanceInfo.existenceStatus === 'EXISTS'
                  }
                  onChange={() => {
                    form.setFieldValue(
                      'carMaintenanceInfo',
                      form.values.carMaintenanceInfo.existenceStatus ===
                        'EXISTS'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'EXISTS',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={
                    form.values.carMaintenanceInfo.existenceStatus ===
                    'NOT_EXIST'
                  }
                  onChange={() => {
                    form.setFieldValue(
                      'carMaintenanceInfo',
                      form.values.carMaintenanceInfo.existenceStatus ===
                        'NOT_EXIST'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'NOT_EXIST',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 營業時間 */}
                {form.values.carMaintenanceInfo.existenceStatus ===
                  'EXISTS' && (
                  <TextInput
                    placeholder={'請輸入營業時間'}
                    key={form.key(
                      'carMaintenanceInfo.operationHoursDescription',
                    )}
                    {...form.getInputProps(
                      'carMaintenanceInfo.operationHoursDescription',
                    )}
                    size={'xs'}
                    styles={{
                      input: { width: 190 },
                    }}
                  />
                )}
              </Group>
            </Stack>
          </Group>

          <Group gap={16} style={{ paddingBottom: 8 }}>
            {/* 便利商店 */}
            <Stack gap={8}>
              <Group gap={0}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  便利商店
                </div>
              </Group>
              <Group gap={12} w={305.5} h={32}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={
                    form.values.convenienceStoreInfo.existenceStatus ===
                    'EXISTS'
                  }
                  onChange={() => {
                    form.setFieldValue(
                      'convenienceStoreInfo',
                      form.values.convenienceStoreInfo.existenceStatus ===
                        'EXISTS'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'EXISTS',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={
                    form.values.convenienceStoreInfo.existenceStatus ===
                    'NOT_EXIST'
                  }
                  onChange={() => {
                    form.setFieldValue(
                      'convenienceStoreInfo',
                      form.values.convenienceStoreInfo.existenceStatus ===
                        'NOT_EXIST'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'NOT_EXIST',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 營業時間 */}
                {form.values.convenienceStoreInfo.existenceStatus ===
                  'EXISTS' && (
                  <TextInput
                    placeholder={'請輸入營業時間'}
                    key={form.key(
                      'convenienceStoreInfo.operationHoursDescription',
                    )}
                    {...form.getInputProps(
                      'convenienceStoreInfo.operationHoursDescription',
                    )}
                    size={'xs'}
                    styles={{
                      input: { width: 190 },
                    }}
                  />
                )}
              </Group>
            </Stack>
            {/* 加水 */}
            <Stack gap={8}>
              <Group gap={0}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  加水
                </div>
              </Group>
              <Group gap={12} w={305.5} h={32}>
                {/* 有 */}
                <Checkbox
                  radius={'xl'}
                  label={'有'}
                  checked={
                    form.values.waterPumpInfo.existenceStatus === 'EXISTS'
                  }
                  onChange={() => {
                    form.setFieldValue(
                      'waterPumpInfo',
                      form.values.waterPumpInfo.existenceStatus === 'EXISTS'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'EXISTS',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 無 */}
                <Checkbox
                  radius={'xl'}
                  label={'無'}
                  checked={
                    form.values.waterPumpInfo.existenceStatus === 'NOT_EXIST'
                  }
                  onChange={() => {
                    form.setFieldValue(
                      'waterPumpInfo',
                      form.values.waterPumpInfo.existenceStatus === 'NOT_EXIST'
                        ? {
                            existenceStatus: 'UNKNOWN',
                            operationHoursDescription: '',
                          }
                        : {
                            existenceStatus: 'NOT_EXIST',
                            operationHoursDescription: '',
                          },
                    );
                  }}
                  styles={{ label: { paddingLeft: 8 } }}
                />
                {/* 營業時間 */}
                {form.values.waterPumpInfo.existenceStatus === 'EXISTS' && (
                  <TextInput
                    placeholder={'請輸入營業時間'}
                    key={form.key('waterPumpInfo.operationHoursDescription')}
                    {...form.getInputProps(
                      'waterPumpInfo.operationHoursDescription',
                    )}
                    size={'xs'}
                    styles={{
                      input: { width: 190 },
                    }}
                  />
                )}
              </Group>
            </Stack>
          </Group>
        </Stack>

        {/* 取消 與 新增 or 更新按鈕 */}
        <Group justify={'flex-end'} mt={'24px'}>
          <Button variant="outline" onClick={handleCancel}>
            關閉
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {submitButtonText}
          </Button>
        </Group>
      </form>
    </>
  );
}
